import { useMemo } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { $initialAccountsStore, $profileFeatureStore } from '@lib/store';
import { AccountUtils, FormatUtils } from '@shared/utils';
import { AccountType, type AccountResponse, type UserProfileResponse, type UserResponse, type UserWithPaymentInfo } from '@shared/api';
import { Button, ErrorMessage, Modal, Multiselect } from '@shared/widgets';

interface IForm {
  accounts: number[];
}
const DEFAULT_VALUES: IForm = { accounts: [] };

interface AddAccountsProps {
  isOpen: boolean;
  user: UserWithPaymentInfo | UserResponse | UserProfileResponse;
  accountsItems: AccountResponse[];
  onClose: () => void;
  onSubmit: (data: { userIds: string; accountIds: number[] }) => void;
}

export const AddAccountsModal = (props: AddAccountsProps) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const userAccountsStore = useUnit($initialAccountsStore.store);

  const { control, formState, handleSubmit, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const { t } = useTranslation();

  const closeHandler = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
  };
  const submitHandler = (data: IForm) => {
    props.onSubmit({ userIds: props.user.id, accountIds: data.accounts });
    closeHandler();
  };

  const accountList = useMemo(() => {
    if (props.isOpen && profileFeatureStore.data?.accountFlows)
      return AccountUtils.filterAccounts(profileFeatureStore.data.accountFlows, userAccountsStore.data);
    return [];
  }, [props.isOpen, profileFeatureStore.data?.accountFlows, userAccountsStore.data]);
  return (
    <Modal isOpen={props.isOpen} onClose={closeHandler} title={t('modals.addAccounts.title')}>
      <form className='flex flex-col gap-6' noValidate onSubmit={handleSubmit(submitHandler)}>
        <Controller
          name='accounts'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <div>
              <Multiselect
                isLoading={userAccountsStore.loading}
                disabled={userAccountsStore.loading}
                items={accountList
                  .filter((acc) => !props.accountsItems.map((a) => a.id).includes(acc.id))
                  .map((acc) => ({
                    value: acc.id,
                    label: acc.type === AccountType.Payment ? acc.accountNumber : FormatUtils.toSliceNumber(acc.accountNumber),
                  }))}
                required
                nullValue={{ isEnable: false, text: t('placeholders.chooseAccounts') }}
                value={value}
                onChange={onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Button fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
          {t('buttons.add')}
        </Button>
      </form>
    </Modal>
  );
};
